/**
 * Con qué ficha va una tarea.
 *
 * Antes esto era un `<input>` de texto donde había que pegar un id a mano —y
 * que ni siquiera se pintaba en el formulario—, así que en la práctica ninguna
 * tarea creada desde el CRM quedaba relacionada con nada. Aquí se busca por
 * nombre y se elige de una lista.
 *
 * Guarda también el nombre (`relatedName`) además del id: la lista de tareas
 * pinta decenas de filas y resolver cada nombre contra su contenedor sería una
 * lectura por fila.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search, X } from 'lucide-react';
import { api } from '@/lib/api';
import type { TaskRelacion } from '@/lib/types';
import { ETIQUETA_RELACION, ICONO_RELACION, rutaDeRelacion } from './tipos';

export interface Relacion {
  relatedType: TaskRelacion | null;
  relatedId: string | null;
  relatedName: string | null;
}

interface Props {
  valor: Relacion;
  onChange: (r: Relacion) => void;
}

interface Resultado {
  id: string;
  nombre: string;
}

/**
 * Las peticiones y los candidatos no se buscan: su tarea se relaciona sola al
 * aceptar la petición o al mover al candidato de estado.
 */
const BUSCABLES: TaskRelacion[] = ['lead', 'owner', 'property', 'empleado'];

async function buscar(tipo: TaskRelacion, texto: string): Promise<Resultado[]> {
  const params = { search: texto };
  if (tipo === 'lead') {
    const leads = await api.getLeads(params);
    return leads.slice(0, 8).map(l => ({ id: l.id, nombre: `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.email || l.id }));
  }
  if (tipo === 'owner') {
    const owners = await api.getOwners(params);
    return owners.slice(0, 8).map(o => ({ id: o.id, nombre: `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email || o.id }));
  }
  if (tipo === 'empleado') {
    const { empleados } = await api.getPersonal({ q: texto });
    return empleados.slice(0, 8).map(e => ({ id: e.id, nombre: e.nombreCompleto }));
  }
  const props = await api.getProperties(params);
  return props.slice(0, 8).map(p => ({ id: p.id, nombre: p.name || p.address?.street || p.id }));
}

export default function SelectorRelacion({ valor, onChange }: Props) {
  const [tipo, setTipo] = useState<TaskRelacion>(
    valor.relatedType && BUSCABLES.includes(valor.relatedType) ? valor.relatedType : 'lead',
  );
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [buscando, setBuscando] = useState(false);
  // Misma guardia de obsolescencia que `useApi`: al teclear rápido puede llegar
  // antes la respuesta de una búsqueda anterior y dejar en la lista lo que no se
  // ha pedido.
  const generacion = useRef(0);

  useEffect(() => {
    if (texto.trim().length < 2) { setResultados([]); return; }

    const mia = ++generacion.current;
    setBuscando(true);
    // Medio segundo de espera: escribir «Ana» son tres pulsaciones y no hacen
    // falta tres consultas al servidor.
    const t = setTimeout(() => {
      buscar(tipo, texto.trim())
        .then(r => { if (mia === generacion.current) setResultados(r); })
        .catch(() => { if (mia === generacion.current) setResultados([]); })
        .finally(() => { if (mia === generacion.current) setBuscando(false); });
    }, 400);

    return () => clearTimeout(t);
  }, [texto, tipo]);

  if (valor.relatedId) {
    const Icono = ICONO_RELACION[valor.relatedType || 'lead'];
    const ruta = rutaDeRelacion(valor);
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-sereno-blue/10">
        <Icono size={15} className="text-sereno-dark/50 flex-shrink-0" />
        <span className="text-sm flex-1 min-w-0 truncate">
          {ruta ? (
            <Link to={ruta} className="hover:underline" title="Abrir la ficha">
              {valor.relatedName || valor.relatedId}
              <ChevronRight size={12} className="inline ml-0.5 -mt-0.5 text-sereno-dark/40" />
            </Link>
          ) : (
            valor.relatedName || valor.relatedId
          )}
          <span className="text-sereno-dark/40 ml-2 text-xs">
            {ETIQUETA_RELACION[valor.relatedType || 'lead']}
          </span>
        </span>
        <button
          type="button"
          onClick={() => { onChange({ relatedType: null, relatedId: null, relatedName: null }); setTexto(''); }}
          className="p-1 rounded hover:bg-white/60 text-sereno-dark/40"
          title="Quitar la relación"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {BUSCABLES.map(t => (
          <button
            key={t}
            type="button"
            onClick={() => { setTipo(t); setResultados([]); }}
            className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
              tipo === t ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-sereno-dark/60 hover:bg-gray-200'
            }`}
          >
            {ETIQUETA_RELACION[t]}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
        <input
          className="input pl-9"
          placeholder={`Buscar ${ETIQUETA_RELACION[tipo].toLowerCase()}…`}
          value={texto}
          onChange={e => setTexto(e.target.value)}
        />
      </div>

      {texto.trim().length >= 2 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-44 overflow-y-auto">
          {buscando && <p className="text-xs text-sereno-dark/40 px-3 py-2">Buscando…</p>}
          {!buscando && resultados.length === 0 && (
            <p className="text-xs text-sereno-dark/40 px-3 py-2">Sin resultados</p>
          )}
          {resultados.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => onChange({ relatedType: tipo, relatedId: r.id, relatedName: r.nombre })}
              className="w-full text-left px-3 py-2 text-sm hover:bg-sereno-blue/20 transition-colors"
            >
              {r.nombre}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
